import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Save, Trash2, Pencil } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Scheda di modifica di una testata: le modifiche diventano effettive SOLO con "Salva modifiche". */
export function MediaEditDialog({ media, open, onOpenChange, categories = [], nations = [], onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!media) { setForm(null); return; }
    setForm({
      name: media.name || "", website: media.website || "", editor_name: media.editor_name || "", editor_email: media.editor_email || "",
      phone: media.phone || "", whatsapp: media.whatsapp || "",
      category: media.category || "", nation: media.nation || "", outlet_type: media.outlet_type || "", description: media.description || "",
      notes: media.notes || "", monthly_reach: media.monthly_reach ?? 0,
      journalists: Array.isArray(media.journalists) ? media.journalists.map((j) => ({ name: j?.name || "", role: j?.role || "", email: j?.email || "" })) : [],
    });
  }, [media]);

  if (!form) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setJ = (i, patch) => set({ journalists: form.journalists.map((j, k) => (k === i ? { ...j, ...patch } : j)) });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Il nome della testata è obbligatorio"); return; }
    if (form.editor_email.trim() && !EMAIL_RE.test(form.editor_email.trim())) { toast.error("Email di redazione non valida"); return; }
    const badJ = form.journalists.find((j) => j.email.trim() && !EMAIL_RE.test(j.email.trim()));
    if (badJ) { toast.error(`Email giornalista non valida: ${badJ.email}`); return; }
    setSaving(true);
    try {
      const payload = { ...form, monthly_reach: Number(form.monthly_reach) || 0,
        journalists: form.journalists.filter((j) => j.name.trim() || j.email.trim()) };
      const { data } = await api.patch(`/media/${media._id}`, payload);
      toast.success(data.drafts_realigned > 0
        ? `Modifiche salvate · ${data.drafts_realigned} bozze PR aggiornate con la nuova email`
        : "Modifiche salvate");
      onSaved?.(data.media || { ...media, ...payload });
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };

  const cats = categories.includes(form.category) || !form.category ? categories : [form.category, ...categories];
  const nats = nations.includes(form.nation) || !form.nation ? nations : [form.nation, ...nations];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-border bg-brema-card" data-testid="media-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground"><Pencil className="h-4 w-4 text-brand" /> Modifica testata · {media?.name}</DialogTitle>
          <DialogDescription>Correggi URL, email di redazione e referenti. Le modifiche diventano effettive solo con <b>Salva modifiche</b> e vengono usate dagli invii successivi (anche massivi).</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-foreground">Nome testata *</Label>
            <Input data-testid="media-edit-name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-foreground">Sito web (URL)</Label>
            <Input data-testid="media-edit-website" value={form.website} onChange={(e) => set({ website: e.target.value })} placeholder="https://www.testata.it" />
            {media?.link_status === "invalid" && <p className="text-[11px] text-amber-600">Link attualmente segnato non valido{media?.link_code ? ` (${media.link_code})` : ""}: correggendo l'URL la segnalazione viene azzerata fino alla prossima verifica.</p>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Referente redazione</Label>
            <Input data-testid="media-edit-editor-name" value={form.editor_name} onChange={(e) => set({ editor_name: e.target.value })} placeholder="Redazione" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Email redazione (destinatario pitch)</Label>
            <Input data-testid="media-edit-editor-email" type="email" value={form.editor_email} onChange={(e) => set({ editor_email: e.target.value })} placeholder="redazione@testata.it" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Telefono redazione</Label>
            <Input data-testid="media-edit-phone" value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="+39 02 1234567" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">WhatsApp redazione (invii WhatsApp)</Label>
            <Input data-testid="media-edit-whatsapp" value={form.whatsapp} onChange={(e) => set({ whatsapp: e.target.value })} placeholder="+39 348 1234567" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Categoria</Label>
            <Select value={form.category || "__none"} onValueChange={(v) => set({ category: v === "__none" ? "" : v })}>
              <SelectTrigger data-testid="media-edit-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">—</SelectItem>
                {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Nazione</Label>
            <Select value={form.nation || "__none"} onValueChange={(v) => set({ nation: v === "__none" ? "" : v })}>
              <SelectTrigger data-testid="media-edit-nation"><SelectValue placeholder="Nazione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">—</SelectItem>
                {nats.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Tipo (Sito, Blog, Rivista…)</Label>
            <Input data-testid="media-edit-type" value={form.outlet_type} onChange={(e) => set({ outlet_type: e.target.value })} placeholder="Sito" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Lettori/mese</Label>
            <Input data-testid="media-edit-reach" type="number" min="0" value={form.monthly_reach} onChange={(e) => set({ monthly_reach: e.target.value })} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-foreground">Descrizione</Label>
            <Textarea data-testid="media-edit-description" value={form.description} onChange={(e) => set({ description: e.target.value })} className="min-h-[60px]" />
          </div>
          <div className="space-y-2 sm:col-span-2 rounded-lg border border-border bg-brema-bg p-3" data-testid="media-edit-journalists">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Giornalisti / referenti aggiuntivi</Label>
              <Button type="button" size="sm" variant="secondary" data-testid="media-edit-add-journalist"
                onClick={() => set({ journalists: [...form.journalists, { name: "", role: "", email: "" }] })}
                className="h-7 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover"><Plus className="mr-1 h-3 w-3" /> Aggiungi</Button>
            </div>
            {form.journalists.length === 0 && <p className="text-[11px] text-muted-foreground">Nessun referente aggiuntivo.</p>}
            {form.journalists.map((j, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_auto] items-center gap-2" data-testid="media-edit-journalist-row">
                <Input value={j.name} onChange={(e) => setJ(i, { name: e.target.value })} placeholder="Nome" className="h-8 text-xs" data-testid="media-edit-journalist-name" />
                <Input value={j.role} onChange={(e) => setJ(i, { role: e.target.value })} placeholder="Ruolo" className="h-8 text-xs" />
                <Input value={j.email} onChange={(e) => setJ(i, { email: e.target.value })} placeholder="email@testata.it" className="h-8 text-xs" data-testid="media-edit-journalist-email" />
                <button type="button" onClick={() => set({ journalists: form.journalists.filter((_, k) => k !== i) })}
                  className="text-muted-foreground hover:text-danger" title="Rimuovi" data-testid="media-edit-journalist-remove"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-foreground">Note interne</Label>
            <Textarea data-testid="media-edit-notes" value={form.notes} onChange={(e) => set({ notes: e.target.value })} className="min-h-[50px]" />
          </div>
        </div>
        <DialogFooter className="mt-2 gap-2">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} data-testid="media-edit-cancel"
            className="rounded-full border border-border bg-transparent hover:bg-brema-hover">Annulla</Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="media-edit-save"
            className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva modifiche
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
